import { useState, useCallback, useRef, memo } from "react";
import { useAppDispatch, useAppSelector } from "stores";
import store from "stores/interfaces";
import {
  Menu,
  MenuItem,
  IconButton,
  Tooltip,
  Divider,
  ListSubheader,
} from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { useTranslation } from "react-i18next";
import { appStateMutate } from "stores/modules/app.state/operations";
import { removeRoomMessageGroup } from "stores/modules/entities.rooms/operations";
import {
  deleteAllMessages,
  downloadRoomLog,
  downloadRoomLogHtml,
  downloadRoomLogJson,
} from "stores/modules/entities.room.messages/operations";
import { getCurrentTabName } from "stores/modules/entities.rooms/selectors";
import { getIsPro } from "stores/modules/app.user/selectors";
import CcfoliaProIcon from "containers/CcfoliaProIcon";

type ChatMenuProps = {
  uid: string;
  roomId: string;
  standalone: boolean;
};

const ChatMenu = ({ uid, roomId, standalone }: ChatMenuProps) => {
  const dispatch = useAppDispatch();
  const [t] = useTranslation();

  const anchorEl = useRef(null);
  const [open, setOpen] = useState(false);
  const tab = useAppSelector((state) =>
    store.getAppState(state, "roomChatTab")
  );
  const tabName = useAppSelector(getCurrentTabName);
  const isGroupTab = useAppSelector((state) => {
    const room = store.getRoomById(state, roomId);
    return room.messageGroups.findIndex((group) => group.id === tab) >= 0;
  });
  const hasEditableRole = useAppSelector(store.getHasEditableRole);

  const isPro = useAppSelector(getIsPro);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const onClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onDeleteAllMessages = useCallback(() => {
    if (
      window.confirm(
        t("全てのルームログデータを削除します。本当に削除しますか？")
      )
    ) {
      setOpen(false);
      dispatch(deleteAllMessages(roomId)).then(() => {
        window.alert(
          t("ルームログ削除後も再ログインするまでメッセージは保持されます")
        );
      });
    }
  }, [roomId, setOpen, t, dispatch]);

  const onClickPopup = useCallback(() => {
    setOpen(false);
    dispatch(
      appStateMutate((state) => {
        state.openRoomDisplays = null;
      })
    );

    const width = 380;
    const height = 640;

    window.open(
      `/rooms/${roomId}/chat`,
      "ccfolia.chat",
      `resizable,scrollbars,status,width=${width},height=${height},left=${
        window.innerWidth * 0.5 - width * 0.5
      },top=${window.innerHeight * 0.5 - height * 0.5}`
    );
  }, [setOpen, dispatch, roomId]);

  const onClickWideMode = useCallback(() => {
    dispatch(
      appStateMutate((state) => {
        state.roomChatWideMode = !state.roomChatWideMode;
      })
    );
  }, [dispatch]);

  const onClickOpenChatTabSetting = useCallback(() => {
    dispatch(
      appStateMutate((state) => {
        state.openChatTabSettings = true;
        state.openChatTabSettingsId = tab;
      })
    );
    onClose();
  }, [tab, dispatch, onClose]);

  const onClickExportLog = useCallback(() => {
    if (!hasEditableRole) {
      return;
    }

    dispatch(
      downloadRoomLog({
        roomId,
        uid,
        includeSecret: true,
        channel: null,
        channelName: "all",
      })
    );
  }, [hasEditableRole, dispatch, roomId, uid]);

  const onClickExportCurrentTabLog = useCallback(() => {
    dispatch(
      downloadRoomLog({
        roomId,
        uid,
        includeSecret: false,
        channel: tab,
        channelName: tabName,
      })
    );
  }, [dispatch, roomId, uid, tab, tabName]);

  const onClickMessageChannelRemove = useCallback(() => {
    if (window.confirm(t("本当に削除しますか？"))) {
      setOpen(false);
      dispatch(removeRoomMessageGroup(tab));
    }
  }, [tab, setOpen, t, dispatch]);

  const onClickExportLogJson = useCallback(() => {
    if (!hasEditableRole) {
      return;
    }
    dispatch(downloadRoomLogJson(roomId));
  }, [hasEditableRole, dispatch, roomId]);

  const onClickExportLogHtml = useCallback(() => {
    if (!hasEditableRole) {
      return;
    }
    dispatch(downloadRoomLogHtml(roomId));
  }, [hasEditableRole, dispatch, roomId]);

  return (
    <>
      <Tooltip title={t("チャットメニュー")}>
        <IconButton onClick={onOpen} ref={anchorEl} edge="end" size="large">
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Menu open={open} anchorEl={anchorEl.current} onClose={onClose}>
        {!standalone && (
          <>
            <MenuItem onClick={onClickPopup}>
              {t("別窓で表示（beta）")}
            </MenuItem>
            <MenuItem onClick={onClickWideMode}>
              {t("ワイドモード切り替え")}
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem onClick={onClickExportCurrentTabLog}>
          {isPro ? (
            <>
              {t("ログ出力+")}
              <CcfoliaProIcon height="12px" style={{ marginLeft: "6px" }} />
            </>
          ) : (
            t("ログ出力")
          )}
        </MenuItem>
        <Divider />
        <ListSubheader
          sx={{
            backgroundColor: "transparent",
            lineHeight: "24px",
            paddingY: "6px",
          }}
          disableSticky
        >
          {t("ルームマスター専用")}
        </ListSubheader>
        <MenuItem onClick={onClickExportLogHtml} disabled={!hasEditableRole}>
          {isPro ? (
            <>
              {t("HTML出力+")}
              <CcfoliaProIcon height="12px" style={{ marginLeft: "6px" }} />
            </>
          ) : (
            t("HTML出力")
          )}
        </MenuItem>
        <MenuItem onClick={onClickExportLogJson} disabled={!hasEditableRole}>
          {isPro ? (
            <>
              {t("JSON出力+")}
              <CcfoliaProIcon height="12px" style={{ marginLeft: "6px" }} />
            </>
          ) : (
            t("JSON出力")
          )}
        </MenuItem>
        <MenuItem onClick={onClickExportLog} disabled={!hasEditableRole}>
          {isPro ? (
            <>
              {t("全ログ出力+（旧）")}
              <CcfoliaProIcon height="12px" style={{ marginLeft: "6px" }} />
            </>
          ) : (
            t("全ログ出力（旧）")
          )}
        </MenuItem>
        <MenuItem
          onClick={onClickOpenChatTabSetting}
          disabled={!isGroupTab || !hasEditableRole}
        >
          {t("タブの編集")}
        </MenuItem>
        <MenuItem
          onClick={onClickMessageChannelRemove}
          disabled={!isGroupTab || !hasEditableRole}
        >
          {t("現在タブ削除")}
        </MenuItem>
        <MenuItem onClick={onDeleteAllMessages} disabled={!hasEditableRole}>
          {t("ルームログ削除")}
        </MenuItem>
      </Menu>
    </>
  );
};

export default memo(ChatMenu);
